import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { ArrowRight, Clock, BookOpen } from "lucide-react";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
const coverUrl = (v) => (v ? (/^https?:\/\//i.test(v) ? v : `${BACKEND_BASE}${v}`) : null);

// Standalone Blog listing page — SEO-friendly index of all Saturn Styling service guides
const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Saturn Styling Blog — Car Tinting, PPF, Wraps & Detailing Guides";
    const meta = document.querySelector('meta[name="description"]');
    const desc = "Expert guides on window tinting, PPF, vehicle wrapping, ceramic coating, sunstrips and more from Saturn Styling — Ireland's premium automotive styling studio in Duleek, Co. Meath.";
    if (meta) meta.setAttribute('content', desc);
    else {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      m.setAttribute('content', desc);
      document.head.appendChild(m);
    }

    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/blog`);
        setArticles(data.articles || []);

        // Inject Blog collection + Breadcrumb JSON-LD
        const items = (data.articles || []).map((a, i) => ({
          "@type": "BlogPosting",
          "position": i + 1,
          "headline": a.title,
          "url": `https://saturnstyling.ie/blog/${a.slug}`,
          "description": a.excerpt,
          "image": a.cover_image
            ? (/^https?:\/\//i.test(a.cover_image) ? a.cover_image : `https://saturnstyling.ie${a.cover_image}`)
            : undefined,
          "datePublished": a.published_at,
        }));
        const blogSchema = {
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Saturn Journal",
          "description": desc,
          "url": "https://saturnstyling.ie/blog",
          "blogPost": items,
        };
        const breadcrumb = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saturnstyling.ie/" },
            { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://saturnstyling.ie/blog" },
          ],
        };
        const upsert = (id, obj) => {
          let el = document.getElementById(id);
          if (!el) {
            el = document.createElement('script');
            el.type = 'application/ld+json';
            el.id = id;
            document.head.appendChild(el);
          }
          el.textContent = JSON.stringify(obj);
        };
        upsert('ld-blog', blogSchema);
        upsert('ld-blog-breadcrumb', breadcrumb);
      } catch (e) {
        console.error("Failed to load blog", e);
      } finally {
        setLoading(false);
      }
    };
    load();

    return () => {
      ['ld-blog', 'ld-blog-breadcrumb'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-saturn-black text-white" data-testid="blog-page">
      {/* Header */}
      <div className="border-b border-slate-800/70 bg-gradient-to-b from-orange-950/20 to-transparent">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <Link to="/" className="text-orange-400 hover:text-orange-300 text-sm mb-6 inline-block" data-testid="blog-back-home">← Back to Saturn Styling</Link>
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-orange-400" />
            <span className="text-orange-400 text-sm uppercase tracking-widest">Guides & Aftercare</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black">
            Saturn <span className="text-orange-400">Journal</span>
          </h1>
          <p className="text-slate-400 mt-4 max-w-2xl text-lg">
            Straight-talking guides on car tinting, PPF, wraps, ceramic coating, sunstrips and aftercare — everything we&apos;ve learned fitting thousands of cars in Ireland.
          </p>
        </div>
      </div>

      {/* Articles grid */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {loading && (
          <p className="text-slate-400 text-center py-12" data-testid="blog-loading">Loading articles…</p>
        )}
        {!loading && articles.length === 0 && (
          <p className="text-slate-400 text-center py-12" data-testid="blog-empty">No articles published yet — check back soon.</p>
        )}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a, idx) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              data-testid={`blog-card-${a.slug}`}
            >
              <Link
                to={`/blog/${a.slug}`}
                className="group block bg-slate-900/60 border border-slate-800 hover:border-orange-500/60 rounded-2xl overflow-hidden h-full transition-all"
              >
                {coverUrl(a.cover_image) ? (
                  <div className="aspect-[16/9] w-full overflow-hidden bg-slate-800">
                    <img src={coverUrl(a.cover_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] w-full bg-gradient-to-br from-orange-500/20 via-slate-800 to-slate-900 flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-orange-400/50" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(a.tags || []).slice(0, 2).map(t => (
                      <span key={t} className="text-[10px] uppercase tracking-wider text-orange-300/80 bg-orange-500/10 border border-orange-500/20 px-2 py-1 rounded">{t}</span>
                    ))}
                  </div>
                  <h2 className="text-lg font-bold text-white group-hover:text-orange-300 transition-colors mb-3 leading-snug">
                    {a.title}
                  </h2>
                  <p className="text-slate-400 text-sm line-clamp-3 mb-5">{a.excerpt}</p>
                  <div className="flex items-center gap-2 text-orange-400 text-sm font-semibold">
                    Read the guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
